import {
  ArrowLeftOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ReloadOutlined,
  EllipsisOutlined,
  UploadOutlined,
  DeleteOutlined,
  FileOutlined,
  LinkOutlined,
  CloseOutlined,
  CheckCircleFilled,
  ShrinkOutlined,
  ExpandAltOutlined,
  FolderOpenOutlined,
  InboxOutlined
} from "@ant-design/icons";
import {
  Spin,
  Button,
  Modal,
  Dropdown,
  Drawer,
  Select,
  Progress,
  message,
  Checkbox
} from "antd";
import dayjs from "dayjs";
import React, { useState, useEffect, useRef } from "react";
import { MdCompareArrows } from "react-icons/md";

import { PageHeader } from "../pageHeader";

import {
  CompareResultsModal,
  CompareSetupModal,
  pollGridDiff,
  startGridDiff
} from "./drawingCompare";

import type {
  CompareResultsStatus,
  CompareSetupValues,
  DrawingCompareConfig,
  GridDiffStatus
} from "./drawingCompare";
import type {
  DocumentLibraryProject,
  SharePointItem,
  SharePointItemVersion
} from "./types";

export interface DocumentLibraryViewProps {
  /** Project list already filtered by the consuming app (e.g. by user region). */
  projects: DocumentLibraryProject[];
  projectsLoading: boolean;
  /**
   * Project `description` to pre-select on mount once `projects` has loaded.
   * Apps that persist the last-used project read it from storage and pass the
   * result here.
   */
  initialProjectKey?: string;
  /** Fired when the user picks a project so apps can persist it to storage. */
  onProjectSelect?: (description: string) => void;

  /**
   * Resolve a project to its SharePoint root folder, or `null` when it has
   * none yet. The lookup itself happens server-side (the BFF's
   * `folders/resolve`), so the client never lists or name-matches the drive
   * root. Only called for projects that have an `opportunityNo`.
   */
  resolveProjectFolder: (
    project: DocumentLibraryProject
  ) => Promise<SharePointItem | null>;

  /**
   * Fired whenever the active folder changes. The parent uses the folder id
   * to drive `useGetSharepointItems(folderId)` and pass the result back via
   * the `folderItems*` props.
   */
  onCurrentFolderChange: (folder: SharePointItem | null) => void;
  folderItems: SharePointItem[] | undefined;
  folderItemsLoading: boolean;
  folderItemsError: unknown;
  refetchFolderItems: () => void;

  /**
   * Fired when the user opens the Versions drawer for an item. The parent
   * drives `useGetSharepointItemVersions(itemId)` from this.
   */
  onVersionItemChange: (item: SharePointItem | null) => void;
  itemVersions: SharePointItemVersion[] | undefined;
  itemVersionsLoading: boolean;
  itemVersionsError: unknown;

  /** Fetch raw bytes for a file (or a specific historical version). */
  loadFileBlob: (itemId: string, versionId: string | null) => Promise<Blob>;
  /** Upload a single file into `folderId` with progress reporting. */
  uploadFile: (
    file: File,
    folderId: string,
    onProgress: (progress: number) => void
  ) => Promise<void>;

  /**
   * Optional drawing-compare feature config. When this prop is omitted the
   * Compare button is hidden and the feature is fully disabled — that's the
   * intended toggle for hiding compare in apps that shouldn't expose it
   * (e.g. MJ-Tracker). Passing the config opts the consumer in.
   */
  drawingCompare?: DrawingCompareConfig;

  /**
   * Fired by the **Back to Dashboard** button. Apps own their routing, so they
   * decide where "dashboard" is. Omit to hide the button.
   */
  onBackToDashboard?: () => void;
}

const isPdfItem = (item: SharePointItem): boolean => {
  const mimeType = item.file?.mimeType || "";
  return mimeType.includes("pdf");
};

/**
 * Compare is PDF-only — the grid-diff service expects two PDFs.
 * Image-vs-image comparisons are no longer supported.
 */
const isComparableItem = (item: SharePointItem): boolean => {
  return isPdfItem(item);
};

interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: string;
}

const getFileIcon = (item: SharePointItem): string => {
  if (item.folder) return "📁";

  const mimeType = item.file?.mimeType || "";
  if (mimeType.includes("image")) return "🖼️";
  if (mimeType.includes("pdf")) return "📄";
  if (mimeType.includes("word")) return "📝";
  if (mimeType.includes("excel")) return "📊";
  if (mimeType.includes("powerpoint")) return "📽️";
  if (mimeType.includes("text")) return "📄";

  return "📎";
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

/**
 * Image preview with zoom/pan. Defined at **module scope** (not inside
 * `DocumentLibraryView`) so its component identity is stable across the
 * parent's re-renders — otherwise toggling state like `previewMaximized`
 * would remount it and re-run the load effect (re-fetching the blob).
 * `loadFileBlob` is injected as a prop because the lib stays data-agnostic.
 */
const ImagePreview: React.FC<{
  item: SharePointItem;
  version: string | null;
  loadFileBlob: DocumentLibraryViewProps["loadFileBlob"];
}> = ({ item, version, loadFileBlob }) => {
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const blob = await loadFileBlob(item.id, version);
        const imageUrl = URL.createObjectURL(blob);
        setImageSrc(imageUrl);
      } catch (error) {
        console.error("Error loading image:", error);
        setImageError(true);
      }
    };

    load();

    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, version]);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.5, Math.min(scale * delta, 4));
    setScale(newScale);

    if (newScale <= 1) {
      setTranslateX(0);
      setTranslateY(0);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - translateX, y: e.clientY - translateY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      const newTranslateX = e.clientX - dragStart.x;
      const newTranslateY = e.clientY - dragStart.y;

      const maxTranslateX =
        ((scale - 1) * (containerRef.current?.clientWidth || 0)) / 2;
      const maxTranslateY =
        ((scale - 1) * (containerRef.current?.clientHeight || 0)) / 2;

      setTranslateX(
        Math.max(-maxTranslateX, Math.min(maxTranslateX, newTranslateX))
      );
      setTranslateY(
        Math.max(-maxTranslateY, Math.min(maxTranslateY, newTranslateY))
      );
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetZoom = () => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  };

  return (
    <div
      className="w-full h-full bg-gray-100 overflow-hidden relative"
      ref={containerRef}
    >
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <Button
          icon={<ZoomInOutlined />}
          title="Zoom In"
          onClick={() => setScale(Math.min(scale * 1.2, 4))}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            color: "black",
            border: "1px solid #ccc"
          }}
        />
        <Button
          icon={<ZoomOutOutlined />}
          title="Zoom Out"
          onClick={() => setScale(Math.max(scale * 0.8, 0.5))}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            color: "black",
            border: "1px solid #ccc"
          }}
        />
        <Button
          icon={<ReloadOutlined />}
          title="Reset Zoom"
          onClick={resetZoom}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            color: "black",
            border: "1px solid #ccc"
          }}
        />
      </div>

      {!imageLoaded && !imageError && (
        <div className="flex items-center justify-center min-h-[100px]">
          <Spin />
        </div>
      )}

      {imageError && (
        <div className="flex items-center justify-center h-full text-gray-600">
          <div className="text-center">
            <p className="mb-4">Failed to load image</p>
          </div>
        </div>
      )}

      <div
        className="flex items-center justify-center h-full cursor-grab active:cursor-grabbing"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
          transition: isDragging ? "none" : "transform 0.2s ease-out"
        }}
      >
        {imageSrc && (
          <img
            ref={imageRef}
            src={imageSrc}
            alt={item.name}
            className="max-w-full max-h-full object-contain"
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              console.error("Image load error for:", item.name);
              setImageError(true);
            }}
            draggable={false}
            style={{ display: imageLoaded ? "block" : "none" }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * PDF preview (iframe). Module-scoped for the same reason as `ImagePreview` —
 * a stable identity prevents remount-on-re-render, which was causing the PDF
 * to load twice when the modal was maximized.
 */
const PDFPreview: React.FC<{
  item: SharePointItem;
  version: string | null;
  loadFileBlob: DocumentLibraryViewProps["loadFileBlob"];
}> = ({ item, version, loadFileBlob }) => {
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await loadFileBlob(item.id, version);
        if (!data || data.size === 0) {
          throw new Error("Empty PDF data received");
        }

        const blob = new Blob([data], { type: "application/pdf" });
        const pdfObjectUrl = URL.createObjectURL(blob);
        setPdfUrl(pdfObjectUrl);
      } catch (err) {
        setError(
          `Failed to load PDF: ${
            err instanceof Error ? err.message : "Unknown error"
          }`
        );
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, version]);

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center min-h-[100px]">
        <Spin tip="Loading PDF..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-auto bg-gray-100 h-full">
      <div
        className="flex justify-center h-full"
        style={{ transformOrigin: "top center" }}
      >
        <iframe
          ref={iframeRef}
          src={pdfUrl}
          className="w-full h-full min-h-[600px] border border-gray-300 shadow-lg bg-white"
          title={`PDF: ${item.name}`}
          style={{
            transformOrigin: "top center",
            transition: "transform 0.2s ease-out"
          }}
        />
      </div>
    </div>
  );
};

/**
 * Where the selected project's root-folder lookup stands. `idle` until a
 * project is picked; `error` means the lookup itself failed (as opposed to
 * `not-found`, a successful answer of "this project has no folder yet").
 */
type ProjectFolderStatus =
  | "idle"
  | "resolving"
  | "found"
  | "not-found"
  | "error";

interface NavigationEntry {
  id: string | null;
  name: string;
  item: SharePointItem | null;
}

/** Breadcrumb sentinel above every project's root folder. */
const ROOT_HISTORY_ENTRY: NavigationEntry = { id: null, name: "", item: null };

/** The mutually exclusive states of the main content panel. */
type ContentState =
  | "loading"
  | "no-project"
  | "folder-error"
  | "folder-not-found"
  | "items-error"
  | "items";

/**
 * Centred message shown inside the main content panel whenever there is no
 * file list to render (nothing selected yet, no folder, empty folder). Keeps
 * every "nothing to show here" state on the same vertical rhythm.
 */
const PanelMessage: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
}> = ({ icon, title, description }) => (
  <div className="flex flex-col justify-center items-center py-16 gap-2 text-center">
    <span className="text-3xl text-gray-300">{icon}</span>
    <p className="m-0 text-gray-700 font-medium">{title}</p>
    {description && (
      <p className="m-0 text-sm text-gray-500 max-w-md">{description}</p>
    )}
  </div>
);

/**
 * SharePoint-backed Document Library — project picker + folder navigator +
 * image/PDF previews + versions drawer + uploads + optional compare/localize.
 *
 * Pure presentational re: data: takes already-fetched data and explicit
 * callbacks (`loadFileBlob`, `uploadFile`) — keeps MSAL/axios/storage out of
 * `libs/shared/ui` per the lib's contribution rules.
 */
export const DocumentLibraryView: React.FC<DocumentLibraryViewProps> = ({
  projects,
  projectsLoading,
  initialProjectKey,
  onProjectSelect,
  resolveProjectFolder,
  onCurrentFolderChange,
  folderItems,
  folderItemsLoading,
  folderItemsError,
  refetchFolderItems,
  onVersionItemChange,
  itemVersions,
  itemVersionsLoading,
  itemVersionsError,
  loadFileBlob,
  uploadFile,
  drawingCompare,
  onBackToDashboard
}) => {
  const [project, setProject] = React.useState<string>();
  const [currentFolder, setCurrentFolder] =
    React.useState<SharePointItem | null>(null);
  const [navigationHistory, setNavigationHistory] = React.useState<
    NavigationEntry[]
  >([ROOT_HISTORY_ENTRY]);

  const [previewItem, setPreviewItem] = React.useState<SharePointItem | null>(
    null
  );
  const [previewType, setPreviewType] = React.useState<"image" | "pdf" | null>(
    null
  );
  const [previewVersion, setPreviewVersion] = React.useState<string | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [previewMaximized, setPreviewMaximized] = React.useState(false);

  const [versionItem, setVersionItem] = React.useState<SharePointItem | null>(
    null
  );
  const [isVersionsDrawerOpen, setIsVersionsDrawerOpen] = React.useState(false);
  const [folderStatus, setFolderStatus] =
    React.useState<ProjectFolderStatus>("idle");
  const latestSelectionRef = useRef(0);
  const [didAutoSelect, setDidAutoSelect] = React.useState(false);

  // Upload state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelectedItems, setCompareSelectedItems] = useState<
    SharePointItem[]
  >([]);
  const [isCompareSetupOpen, setIsCompareSetupOpen] = useState(false);
  const [isCompareResultsOpen, setIsCompareResultsOpen] = useState(false);
  const [compareStatus, setCompareStatus] =
    useState<CompareResultsStatus>("loading");
  const [compareSnapshot, setCompareSnapshot] = useState<GridDiffStatus | null>(
    null
  );
  const [compareError, setCompareError] = useState<string | null>(null);
  const compareAbortRef = useRef<AbortController | null>(null);

  const updateCurrentFolder = (folder: SharePointItem | null) => {
    setCurrentFolder(folder);
    onCurrentFolderChange(folder);
  };

  const updateVersionItem = (item: SharePointItem | null) => {
    setVersionItem(item);
    onVersionItemChange(item);
  };

  /**
   * Selects a project and opens its SharePoint root folder.
   *
   * Resolution is async, so a quick A → B switch can see A's answer arrive
   * after B's — `latestSelectionRef` lets only the most recent selection
   * write its result. A project without an `opportunityNo` has no folder by
   * definition (folders are keyed on it), so it skips the lookup entirely.
   */
  const selectProject = async (target: DocumentLibraryProject) => {
    const selection = ++latestSelectionRef.current;
    const isCurrent = () => selection === latestSelectionRef.current;

    setProject(target.description);
    updateCurrentFolder(null);
    setNavigationHistory([ROOT_HISTORY_ENTRY]);

    if (!target.opportunityNo) {
      setFolderStatus("not-found");
      return;
    }

    setFolderStatus("resolving");
    try {
      const folder = await resolveProjectFolder(target);
      if (!isCurrent()) return;

      if (folder) {
        setFolderStatus("found");
        updateCurrentFolder(folder);
        setNavigationHistory([
          ROOT_HISTORY_ENTRY,
          { id: folder.id, name: folder.name, item: folder }
        ]);
      } else {
        setFolderStatus("not-found");
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error resolving project folder:", error);
      setFolderStatus("error");
    }
  };

  const selectedProject = projects.find((p) => p.description === project);

  // Pre-select the saved project once the project list loads.
  React.useEffect(() => {
    if (didAutoSelect) return;
    if (!initialProjectKey) return;
    if (!projects.length) return;

    const target = projects.find((p) => p.description === initialProjectKey);
    if (target) {
      void selectProject(target);
      setDidAutoSelect(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, initialProjectKey, didAutoSelect]);

  const hasComparableItems = React.useMemo(() => {
    if (!folderItems || folderItems.length < 2) return false;
    return folderItems.filter(isPdfItem).length >= 2;
  }, [folderItems]);

  const cancelComparePolling = () => {
    compareAbortRef.current?.abort();
    compareAbortRef.current = null;
  };

  // Reset compare state when folder changes
  useEffect(() => {
    setCompareMode(false);
    setCompareSelectedItems([]);
    setCompareSnapshot(null);
    setCompareError(null);
    cancelComparePolling();
  }, [currentFolder?.id]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      cancelComparePolling();
    };
  }, []);

  const handleCompareToggleItem = (item: SharePointItem) => {
    setCompareSelectedItems((prev) => {
      const isSelected = prev.some((i) => i.id === item.id);
      if (isSelected) {
        return prev.filter((i) => i.id !== item.id);
      }
      if (prev.length >= 2) {
        message.warning("You can only select 2 files to compare");
        return prev;
      }
      return [...prev, item];
    });
  };

  const runCompare = async (values: CompareSetupValues) => {
    if (!drawingCompare) return;
    if (compareSelectedItems.length !== 2) return;

    cancelComparePolling();
    const abort = new AbortController();
    compareAbortRef.current = abort;

    setIsCompareSetupOpen(false);
    setIsCompareResultsOpen(true);
    setCompareStatus("loading");
    setCompareError(null);
    setCompareSnapshot(null);

    try {
      const [fileA, fileB] = compareSelectedItems;

      const { job_id } = await startGridDiff({
        apiUrl: drawingCompare.apiUrl,
        getAuthToken: drawingCompare.getAuthToken,
        fileAItemId: fileA.id,
        fileBItemId: fileB.id,
        // claudeApiKey: values.claudeApiKey,
        promptExtra: values.promptExtra,
        claudeModel: values.claudeModel,
        signal: abort.signal
      });

      const finalSnapshot = await pollGridDiff({
        apiUrl: drawingCompare.apiUrl,
        getAuthToken: drawingCompare.getAuthToken,
        jobId: job_id,
        signal: abort.signal,
        onProgress: setCompareSnapshot
      });

      setCompareSnapshot(finalSnapshot);
      setCompareStatus("done");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setCompareError(err instanceof Error ? err.message : "Comparison failed");
      setCompareStatus("error");
    } finally {
      if (compareAbortRef.current === abort) {
        compareAbortRef.current = null;
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newUploadFiles: UploadFile[] = Array.from(files).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      progress: 0,
      status: "pending" as const
    }));

    setUploadFiles((prev) => [...prev, ...newUploadFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeUploadFile = (id: string) => {
    setUploadFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadSingleFile = async (upload: UploadFile) => {
    if (!currentFolder?.id) {
      throw new Error("No folder selected");
    }

    const updateProgress = (progress: number) => {
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.id === upload.id
            ? { ...f, progress, status: "uploading" as const }
            : f
        )
      );
    };

    await uploadFile(upload.file, currentFolder.id, updateProgress);
  };

  const handleUploadAll = async () => {
    if (!currentFolder?.id) {
      message.error("Please select a folder first");
      return;
    }

    const pendingFiles = uploadFiles.filter(
      (f) => f.status === "pending" || f.status === "error"
    );
    if (pendingFiles.length === 0) {
      message.warning("No files to upload");
      return;
    }

    setIsUploading(true);

    for (const upload of pendingFiles) {
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.id === upload.id
            ? { ...f, status: "uploading" as const, progress: 0 }
            : f
        )
      );

      try {
        await uploadSingleFile(upload);
        setUploadFiles((prev) =>
          prev.map((f) =>
            f.id === upload.id
              ? { ...f, status: "completed" as const, progress: 100 }
              : f
          )
        );
      } catch (error) {
        setUploadFiles((prev) =>
          prev.map((f) =>
            f.id === upload.id
              ? {
                  ...f,
                  status: "error" as const,
                  error:
                    error instanceof Error ? error.message : "Upload failed"
                }
              : f
          )
        );
      }
    }

    setIsUploading(false);
    refetchFolderItems();

    const completedCount =
      uploadFiles.filter((f) => f.status === "completed").length +
      pendingFiles.length;
    message.success(`Upload complete: ${completedCount} file(s) uploaded`);
  };

  const clearCompletedUploads = () => {
    setUploadFiles((prev) => prev.filter((f) => f.status !== "completed"));
  };

  const closeUploadModal = () => {
    if (isUploading) {
      message.warning("Please wait for uploads to complete");
      return;
    }
    setIsUploadModalOpen(false);
    setUploadFiles([]);
  };

  const handleItemPress = (item: SharePointItem) => {
    if (item.folder) {
      updateCurrentFolder(item);
      setNavigationHistory((prev) => [
        ...prev,
        { id: item.id, name: item.name, item }
      ]);
    } else {
      const mimeType = item.file?.mimeType || "";

      if (mimeType.includes("image")) {
        setPreviewItem(item);
        setPreviewType("image");
        setIsModalOpen(true);
      } else if (mimeType.includes("pdf")) {
        setPreviewItem(item);
        setPreviewType("pdf");
        setIsModalOpen(true);
      }
    }
  };

  const handleBackPress = () => {
    const newHistory = navigationHistory.slice(0, -1);
    const previousFolder = newHistory[newHistory.length - 1];
    setNavigationHistory(newHistory);
    updateCurrentFolder(previousFolder.item);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPreviewItem(null);
    setPreviewType(null);
    setPreviewVersion(null);
    setPreviewMaximized(false);
  };

  // Height of the fixed-height preview body. When maximized the modal content
  // is a full-viewport (100vh) flex column, so the body fills it (100%);
  // otherwise it's a fixed 80vh slice. Drives the Image/PDF preview height —
  // bump this (not modal `style.top`) to make the preview taller. See
  // libs/shared/ui/CLAUDE.md → "Preview modal layout".
  const previewPanelHeight = previewMaximized ? "100%" : "80vh";

  const renderItem = ({ item }: { item: SharePointItem }) => {
    const comparable = isComparableItem(item);
    const isSelected = compareSelectedItems.some((i) => i.id === item.id);
    const showCheckbox = compareMode && comparable;

    return (
      <div
        key={item.id}
        onClick={() => {
          if (compareMode && comparable) {
            handleCompareToggleItem(item);
          } else {
            handleItemPress(item);
          }
        }}
        className={`cursor-pointer flex items-center p-2 rounded-lg ${
          isSelected ? "bg-blue-100 border border-blue-400" : "bg-neutral-200"
        }`}
      >
        {showCheckbox && (
          <Checkbox
            checked={isSelected}
            className="mr-2"
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleCompareToggleItem(item)}
          />
        )}
        <p className="text-2xl m-0 mr-2">{getFileIcon(item)}</p>
        <div className="flex-1">
          <p className="text-base font-medium text-gray-900 m-0 text-left">
            {item.name}
          </p>
          <p className="text-sm text-gray-500 m-0 text-left">
            {item.folder
              ? `${item.folder.childCount} items`
              : `${formatFileSize(item.size)} • ${formatDate(
                  item.lastModifiedDateTime
                )}`}
          </p>
        </div>
        {isSelected && (
          <CheckCircleFilled className="text-blue-600 text-lg mr-2" />
        )}
        {item.file?.mimeType &&
          ["image/jpeg", "application/pdf"].includes(item.file.mimeType) && (
            <div onClick={(e) => e.stopPropagation()}>
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "versions",
                      label: "Versions",
                      onClick: () => {
                        updateVersionItem(item);
                        setIsVersionsDrawerOpen(true);
                      }
                    }
                  ]
                }}
              >
                <Button size="small" icon={<EllipsisOutlined />} />
              </Dropdown>
            </div>
          )}
      </div>
    );
  };

  const renderBreadcrumb = () =>
    navigationHistory.length > 1 ? (
      <div className="flex items-center pb-4">
        {navigationHistory.length > 2 && (
          <button
            onClick={handleBackPress}
            className="cursor-pointer p-0 bg-transparent mr-2 flex items-center gap-x-1 border-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="#2563eb"
              className="size-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>

            <p className="text-blue-600 text-base m-0">Back</p>
          </button>
        )}
        <p className="m-0 text-base font-medium text-gray-700">
          {navigationHistory.map((item) => item.name).join(" / ")}
        </p>
      </div>
    ) : null;

  /**
   * Which of the mutually exclusive states the main content panel is in.
   * Resolved top-down, so each state is only reached once its predecessors
   * are ruled out: project list or folder lookup still loading → nothing
   * picked yet → the folder lookup failed → the project has no SharePoint
   * folder → its files are loading → the file request failed → otherwise the
   * folder's contents.
   */
  const contentState: ContentState =
    projectsLoading || folderStatus === "resolving"
      ? "loading"
      : !project
        ? "no-project"
        : folderStatus === "error"
          ? "folder-error"
          : folderStatus === "not-found"
            ? "folder-not-found"
            : folderItemsLoading
              ? "loading"
              : folderItemsError
                ? "items-error"
                : "items";

  const showCompareButton =
    !!drawingCompare &&
    !!navigationHistory?.[2]?.name?.toLowerCase()?.includes("drawing") &&
    hasComparableItems &&
    !compareMode;

  return (
    <div className="min-h-[calc(100vh-128px)] flex flex-col">
      <PageHeader
        title="Document Library"
        description="Browse and manage opportunity documents from SharePoint"
        extra={
          currentFolder && (
            <div className="flex items-center space-x-3">
              <Button
                type="default"
                icon={<LinkOutlined />}
                onClick={() => {
                  if (currentFolder) {
                    const link = document.createElement("a");
                    link.href = currentFolder.webUrl;
                    link.target = "_blank";
                    link.rel = "noopener noreferrer";
                    link.click();
                  }
                }}
              >
                View in SharePoint
              </Button>
              {showCompareButton && (
                <Button
                  type="primary"
                  icon={<MdCompareArrows />}
                  onClick={() => setCompareMode(true)}
                >
                  Compare
                </Button>
              )}
              {compareMode && (
                <Button
                  danger
                  onClick={() => {
                    setCompareMode(false);
                    setCompareSelectedItems([]);
                  }}
                >
                  Cancel Compare
                </Button>
              )}
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setIsUploadModalOpen(true)}
              >
                Upload Files
              </Button>
              {onBackToDashboard && (
                <Button
                  type="default"
                  icon={<ArrowLeftOutlined />}
                  onClick={onBackToDashboard}
                >
                  Back to Dashboard
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="bg-white p-4 rounded-lg mb-4">
        <Select
          size="large"
          showSearch
          optionFilterProp="label"
          loading={projectsLoading}
          disabled={projectsLoading}
          placeholder={projectsLoading ? "Loading..." : "Select a project"}
          value={project}
          options={projects.map((p) => ({
            key: p.no,
            value: p.description,
            label: p.no ? `${p.no} - ${p.description}` : p.description
          }))}
          onSelect={(value: string) => {
            const target = projects.find((p) => p.description === value);
            if (!target) return;
            onProjectSelect?.(value);
            void selectProject(target);
          }}
          className="min-w-80"
        />
      </div>

      {/* Main content — always a white panel, so the area under the project
          picker is never a bare grey gap. Every state renders a centred
          message inside it. */}
      <div className="flex-1 bg-white p-6 rounded-lg min-h-96">
        {contentState === "no-project" && (
          <PanelMessage
            icon={<FolderOpenOutlined />}
            title="Select a project to view its documents"
            description="Choose a project from the dropdown above to browse its SharePoint files."
          />
        )}

        {contentState === "loading" && (
          <div className="flex flex-col justify-center items-center py-16 gap-4">
            <Spin size="large" />
            <p className="m-0 text-gray-500">Loading documents…</p>
          </div>
        )}

        {contentState === "folder-error" && (
          <div className="flex flex-col justify-center items-center py-16 gap-3">
            <p className="m-0 text-red-600">
              Couldn&apos;t find this project&apos;s SharePoint folder.
            </p>
            <Button
              type="primary"
              onClick={() => selectedProject && selectProject(selectedProject)}
            >
              Retry
            </Button>
          </div>
        )}

        {contentState === "folder-not-found" && (
          <PanelMessage
            icon={<FolderOpenOutlined />}
            title="No SharePoint folder found for this project"
            description="Documents will appear here once a folder has been set up in SharePoint."
          />
        )}

        {contentState === "items-error" && (
          <div className="flex flex-col justify-center items-center py-16 gap-3">
            <p className="m-0 text-red-600">
              Couldn&apos;t load this project&apos;s files.
            </p>
            <Button type="primary" onClick={() => refetchFolderItems()}>
              Retry
            </Button>
          </div>
        )}

        {contentState === "items" && (
          <div>
            {renderBreadcrumb()}
            {compareMode && (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <MdCompareArrows className="text-blue-600 text-xl" />
                  <span className="text-sm text-blue-800 font-medium">
                    Select 2 PDF files to compare
                    {compareSelectedItems.length > 0 &&
                      ` (${compareSelectedItems.length}/2 selected)`}
                  </span>
                  {compareSelectedItems.map(({ id, name }: SharePointItem) => (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                    >
                      {name}
                      <CloseOutlined
                        className="cursor-pointer text-[10px]"
                        onClick={() =>
                          setCompareSelectedItems((prev) =>
                            prev.filter((i) => i.id !== id)
                          )
                        }
                      />
                    </span>
                  ))}
                </div>
                {compareSelectedItems.length === 2 && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => setIsCompareSetupOpen(true)}
                  >
                    Compare selected files
                  </Button>
                )}
              </div>
            )}
            {folderItems?.length ? (
              <div className="flex flex-col gap-y-1">
                {folderItems.map((item) => renderItem({ item }))}
              </div>
            ) : (
              <PanelMessage
                icon={<InboxOutlined />}
                title="This folder is empty"
                description="Upload a file to get started, or open a different folder."
              />
            )}
          </div>
        )}
      </div>

      <Modal
        open={isModalOpen}
        onCancel={handleCloseModal}
        title={
          <>
            <span className="pr-8">{previewItem?.name}</span>
            {/* Anchored to `.ant-modal-content` (position:relative) at the same
                `top` as antd's close X, one slot to its left, so the two icons
                line up exactly. */}
            <button
              type="button"
              onClick={() => setPreviewMaximized((m) => !m)}
              className="flex items-center justify-center text-gray-500 hover:text-gray-800 bg-transparent border-none cursor-pointer p-0"
              style={{
                position: "absolute",
                top: 16,
                right: 56,
                width: 22,
                height: 22
              }}
              title={previewMaximized ? "Restore" : "Maximize"}
              aria-label={previewMaximized ? "Restore modal" : "Maximize modal"}
            >
              {previewMaximized ? <ShrinkOutlined /> : <ExpandAltOutlined />}
            </button>
          </>
        }
        footer={null}
        maskClosable={true}
        keyboard={true}
        width={previewMaximized ? "100vw" : "80vw"}
        centered={!previewMaximized}
        zIndex={10000}
        style={
          previewMaximized
            ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 }
            : { maxWidth: 1600 }
        }
        styles={
          previewMaximized
            ? {
                content: {
                  height: "100vh",
                  borderRadius: 0,
                  display: "flex",
                  flexDirection: "column"
                },
                body: { flex: 1, minHeight: 0, overflow: "hidden" }
              }
            : undefined
        }
      >
        {/* Fixed-height flex column so the preview stays the same size and the
            Image/PDF panel fills whatever space is left (see CLAUDE.md). */}
        <div className="flex flex-col" style={{ height: previewPanelHeight }}>
          {previewItem && previewType === "image" && (
            <div className="flex-1 min-h-0">
              <ImagePreview
                item={previewItem}
                version={previewVersion}
                loadFileBlob={loadFileBlob}
              />
            </div>
          )}
          {previewItem && previewType === "pdf" && (
            <div className="flex-1 min-h-0">
              <PDFPreview
                item={previewItem}
                version={previewVersion}
                loadFileBlob={loadFileBlob}
              />
            </div>
          )}
        </div>
      </Modal>

      <Drawer
        open={isVersionsDrawerOpen}
        onClose={() => {
          setIsVersionsDrawerOpen(false);
          updateVersionItem(null);
        }}
        size="large"
        title={`Versions of "${versionItem?.name}"`}
        styles={{
          header: { padding: "16px" },
          body: { padding: "16px", paddingTop: "8px" }
        }}
      >
        {Boolean(itemVersionsError) && (
          <p className="text-center">Failed to load versions.</p>
        )}
        {itemVersionsLoading && (
          <div className="flex justify-center items-center">
            <Spin />
          </div>
        )}
        {itemVersions && itemVersions.length > 0 && (
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="p-1">Version</th>
                <th className="p-1">Modified</th>
                <th className="p-1">Size</th>
              </tr>
            </thead>
            <tbody>
              {itemVersions.map(
                (
                  { id, lastModifiedDateTime, size }: SharePointItemVersion,
                  index: number
                ) => (
                  <tr key={id}>
                    <td className="p-1">
                      <span
                        className="text-blue-500 cursor-pointer hover:underline"
                        onClick={() => {
                          const mimeType = versionItem?.file?.mimeType || "";

                          if (mimeType.includes("image")) {
                            if (index !== 0) setPreviewVersion(id);
                            setPreviewItem(versionItem);
                            setPreviewType("image");
                            setIsModalOpen(true);
                          } else if (mimeType.includes("pdf")) {
                            if (index !== 0) setPreviewVersion(id);
                            setPreviewItem(versionItem);
                            setPreviewType("pdf");
                            setIsModalOpen(true);
                          }
                        }}
                      >
                        {id}
                      </span>
                    </td>
                    <td className="p-1">
                      {dayjs(lastModifiedDateTime).format("DD/MM/YYYY h:mm A")}
                    </td>
                    <td className="p-1">{formatFileSize(size)}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        )}
      </Drawer>

      <Modal
        open={isUploadModalOpen}
        onCancel={closeUploadModal}
        title="Upload Files to SharePoint"
        footer={null}
        width={600}
        maskClosable={!isUploading}
        closable={!isUploading}
      >
        <div className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const files = e.dataTransfer.files;
              if (files.length > 0) {
                const newUploadFiles: UploadFile[] = Array.from(files).map(
                  (file) => ({
                    id: `${file.name}-${Date.now()}-${Math.random()
                      .toString(36)
                      .substring(2, 11)}`,
                    file,
                    progress: 0,
                    status: "pending" as const
                  })
                );
                setUploadFiles((prev) => [...prev, ...newUploadFiles]);
              }
            }}
          >
            <UploadOutlined className="text-4xl text-gray-400 mb-2" />
            <p className="text-gray-600 m-0">
              Click to select files or drag and drop
            </p>
            <p className="text-gray-400 text-sm m-0 mt-1">
              Multiple files supported
            </p>
          </div>

          {uploadFiles.length > 0 && (
            <div className="max-h-64 overflow-y-auto space-y-2">
              {uploadFiles.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <FileOutlined className="text-xl text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 m-0 truncate">
                      {upload.file.name}
                    </p>
                    <p className="text-xs text-gray-500 m-0">
                      {formatFileSize(upload.file.size)}
                    </p>
                    {upload.status === "uploading" && (
                      <Progress
                        percent={upload.progress}
                        size="small"
                        status="active"
                        className="m-0 mt-1"
                      />
                    )}
                    {upload.status === "completed" && (
                      <p className="text-xs text-green-600 m-0 mt-1">
                        Uploaded successfully
                      </p>
                    )}
                    {upload.status === "error" && (
                      <p className="text-xs text-red-600 m-0 mt-1">
                        {upload.error || "Upload failed"}
                      </p>
                    )}
                  </div>
                  {upload.status !== "uploading" && (
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeUploadFile(upload.id)}
                      className="text-gray-400 hover:text-red-500"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center pt-2">
            <div>
              {uploadFiles.some((f) => f.status === "completed") && (
                <Button
                  type="link"
                  onClick={clearCompletedUploads}
                  className="p-0"
                >
                  Clear completed
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={closeUploadModal} disabled={isUploading}>
                {isUploading ? "Uploading..." : "Close"}
              </Button>
              <Button
                type="primary"
                onClick={handleUploadAll}
                disabled={
                  isUploading ||
                  uploadFiles.filter(
                    (f) => f.status === "pending" || f.status === "error"
                  ).length === 0
                }
                loading={isUploading}
              >
                Upload{" "}
                {uploadFiles.filter(
                  (f) => f.status === "pending" || f.status === "error"
                ).length > 0
                  ? `(${
                      uploadFiles.filter(
                        (f) => f.status === "pending" || f.status === "error"
                      ).length
                    })`
                  : ""}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <CompareSetupModal
        open={isCompareSetupOpen}
        files={compareSelectedItems}
        onCancel={() => setIsCompareSetupOpen(false)}
        onStart={runCompare}
      />

      <CompareResultsModal
        open={isCompareResultsOpen}
        fileAName={compareSelectedItems[0]?.name ?? ""}
        fileBName={compareSelectedItems[1]?.name ?? ""}
        status={compareStatus}
        snapshot={compareSnapshot}
        errorMessage={compareError}
        onClose={() => {
          cancelComparePolling();
          setIsCompareResultsOpen(false);
          setCompareSnapshot(null);
          setCompareError(null);
        }}
      />
    </div>
  );
};
